import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { getTypenForKategorie } from "./enums";

const useStyles = makeStyles({
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "10px",
    "@media (min-width: 600px)": {
      gridTemplateColumns: "repeat(3, 1fr)",
    },
    "@media (min-width: 900px)": {
      gridTemplateColumns: "repeat(4, 1fr)",
    },
  },
  tile: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    textAlign: "center",
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "10px",
    paddingRight: "10px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    rowGap: "6px",
    ...shorthands.border("2px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    color: BRAND.ink,
    minHeight: "94px",
    transitionProperty: "border-color, background-color, transform",
    transitionDuration: "150ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      backgroundColor: "#f5eef6",
    },
  },
  tileActive: {
    ...shorthands.borderColor(BRAND.purple),
    backgroundColor: "#f5eef6",
    boxShadow: `0 0 0 3px ${BRAND.purple}22`,
  },
  iconWrap: {
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  label: {
    lineHeight: 1.2,
    fontWeight: 500,
  },
  empty: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    color: BRAND.muted,
    fontStyle: "italic",
  },
});

const SmallSvg = ({ children }: { children: React.ReactNode }) => (
  <svg width="36" height="32" viewBox="0 0 40 36" fill="none">
    {children}
  </svg>
);

const TYP_ICONS: Record<string, JSX.Element> = {
  Einfamilienhaus: (
    <SmallSvg>
      <path d="M4 32V18l16-12 16 12v14H4z" fill={BRAND.purple} />
      <rect x="16" y="22" width="8" height="10" fill={BRAND.gold} />
    </SmallSvg>
  ),
  Zweifamilienhaus: (
    <SmallSvg>
      <path d="M4 32V18l16-12 16 12v14H4z" fill={BRAND.purple} />
      <rect x="8" y="22" width="6" height="10" fill={BRAND.gold} />
      <rect x="26" y="22" width="6" height="10" fill={BRAND.gold} />
      <line x1="20" y1="6" x2="20" y2="32" stroke={BRAND.bg} strokeWidth="1.5" />
    </SmallSvg>
  ),
  Mehrfamilienhaus: (
    <SmallSvg>
      <rect x="6" y="6" width="28" height="26" fill={BRAND.purple} />
      <rect x="10" y="10" width="5" height="5" fill={BRAND.gold} />
      <rect x="17" y="10" width="5" height="5" fill={BRAND.gold} />
      <rect x="24" y="10" width="5" height="5" fill={BRAND.gold} />
      <rect x="10" y="17" width="5" height="5" fill={BRAND.gold} />
      <rect x="17" y="17" width="5" height="5" fill={BRAND.gold} />
      <rect x="24" y="17" width="5" height="5" fill={BRAND.gold} />
      <rect x="17" y="24" width="5" height="8" fill={BRAND.gold} />
    </SmallSvg>
  ),
  Wohnhochhaus: (
    <SmallSvg>
      <rect x="12" y="2" width="16" height="32" fill={BRAND.purple} />
      {[0, 1, 2, 3, 4].map((i) => (
        <g key={i}>
          <rect x="14" y={4 + i * 6} width="5" height="3" fill={BRAND.gold} />
          <rect x="21" y={4 + i * 6} width="5" height="3" fill={BRAND.gold} />
        </g>
      ))}
    </SmallSvg>
  ),
  Studentenwohnheim: (
    <SmallSvg>
      <rect x="4" y="6" width="32" height="26" fill={BRAND.purple} />
      {[0, 1, 2, 3].map((row) =>
        [0, 1, 2, 3, 4, 5].map((col) => (
          <rect
            key={`${row}-${col}`}
            x={6 + col * 5}
            y={9 + row * 5}
            width="3"
            height="3"
            fill={BRAND.gold}
          />
        ))
      )}
    </SmallSvg>
  ),
  Boardinghouse: (
    <SmallSvg>
      <rect x="4" y="8" width="32" height="24" fill={BRAND.purple} />
      <path d="M4 8h32l-4-4H8z" fill={BRAND.gold} />
      {[0, 1, 2, 3].map((i) => (
        <rect key={i} x={6 + i * 8} y="14" width="6" height="6" fill={BRAND.gold} />
      ))}
      <rect x="16" y="22" width="8" height="10" fill={BRAND.gold} />
    </SmallSvg>
  ),
  "Seniorenheim / Betreutes Wohnen": (
    <SmallSvg>
      <path d="M4 32V18l16-12 16 12v14H4z" fill={BRAND.purple} />
      <circle cx="20" cy="20" r="3" fill={BRAND.gold} />
      <path d="M18 20v6M22 20v6" stroke={BRAND.gold} strokeWidth="1.5" />
    </SmallSvg>
  ),
  "Obdachlosen- /Asylheim / Flüchtlingsheim": (
    <SmallSvg>
      <rect x="4" y="14" width="32" height="18" fill={BRAND.purple} />
      <path d="M4 14L20 4l16 10" stroke={BRAND.gold} strokeWidth="2" fill="none" strokeLinejoin="round" />
      <rect x="16" y="20" width="8" height="12" fill={BRAND.gold} />
    </SmallSvg>
  ),
  Ferienimmobilie: (
    <SmallSvg>
      <path d="M4 28L20 12l16 16" stroke={BRAND.purple} strokeWidth="2" fill="none" />
      <rect x="10" y="20" width="20" height="12" fill={BRAND.purple} />
      <circle cx="32" cy="10" r="3" fill={BRAND.gold} />
      <path d="M16 28v4M24 28v4" stroke={BRAND.gold} strokeWidth="1.5" />
    </SmallSvg>
  ),
  "Logistik/Spedition/Lager": (
    <SmallSvg>
      <rect x="4" y="14" width="32" height="18" fill={BRAND.purple} />
      <rect x="8" y="18" width="8" height="6" fill={BRAND.gold} />
      <rect x="18" y="18" width="8" height="6" fill={BRAND.gold} />
      <rect x="28" y="18" width="6" height="6" fill={BRAND.gold} />
      <rect x="13" y="6" width="14" height="8" fill={BRAND.purple} />
    </SmallSvg>
  ),
  "Büro-/Praxis/ Verwaltung": (
    <SmallSvg>
      <rect x="6" y="4" width="28" height="28" fill={BRAND.purple} />
      {[0, 1, 2].map((row) =>
        [0, 1, 2].map((col) => (
          <rect
            key={`${row}-${col}`}
            x={9 + col * 8}
            y={7 + row * 8}
            width="5"
            height="5"
            fill={BRAND.gold}
          />
        ))
      )}
    </SmallSvg>
  ),
  Handel: (
    <SmallSvg>
      <rect x="4" y="14" width="32" height="18" fill={BRAND.purple} />
      <path d="M4 14L8 6h24l4 8" fill={BRAND.gold} stroke={BRAND.ink} strokeWidth="1" />
      <rect x="14" y="20" width="12" height="12" fill={BRAND.gold} />
    </SmallSvg>
  ),
  Produktion: (
    <SmallSvg>
      <path d="M4 32V18l8 6V18l8 6V18l8 6V14h8v18H4z" fill={BRAND.purple} />
      <rect x="6" y="26" width="3" height="6" fill={BRAND.gold} />
      <rect x="14" y="26" width="3" height="6" fill={BRAND.gold} />
      <rect x="22" y="26" width="3" height="6" fill={BRAND.gold} />
    </SmallSvg>
  ),
  "Hotel oder Beherbergungsbetrieb": (
    <SmallSvg>
      <rect x="4" y="6" width="32" height="26" fill={BRAND.purple} />
      <path d="M14 18h12c0 4-3 6-6 6s-6-2-6-6z" fill={BRAND.gold} />
      <line x1="14" y1="22" x2="26" y2="22" stroke={BRAND.gold} strokeWidth="2" />
    </SmallSvg>
  ),
  Sonstige: (
    <SmallSvg>
      <circle cx="20" cy="18" r="12" fill={BRAND.purple} />
      <text x="20" y="23" fontSize="14" fontWeight="700" fill={BRAND.gold} textAnchor="middle">
        ?
      </text>
    </SmallSvg>
  ),
};

type Props = {
  kategorie: string | undefined;
  value: string;
  onChange: (next: string) => void;
};

const TypPicker = ({ kategorie, value, onChange }: Props) => {
  const styles = useStyles();
  const typen = getTypenForKategorie(kategorie);

  if (kategorie === "Mischnutzung (Wohnen u. Gewerbe)") {
    return (
      <p className={styles.empty}>
        Bei Mischnutzung erfassen wir die Verteilung später pro Einheit — Sie können direkt zum nächsten Schritt.
      </p>
    );
  }
  if (typen.length === 0) {
    return (
      <p className={styles.empty}>
        Bitte wählen Sie zunächst eine Kategorie.
      </p>
    );
  }

  return (
    <div className={styles.grid}>
      {typen.map((t) => {
        const active = value === t;
        const icon = TYP_ICONS[t];
        return (
          <button
            type="button"
            key={t}
            className={mergeClasses(styles.tile, active && styles.tileActive)}
            onClick={() => onChange(t)}
          >
            <div className={styles.iconWrap}>{icon}</div>
            <span className={styles.label}>{t}</span>
          </button>
        );
      })}
    </div>
  );
};

export default TypPicker;
